import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '@shared/ui/main-layout';
import DataTable, { Column } from '@shared/ui/data-table';
import { Tabs } from '../../shared/ui/tabs';
import NotFound from '../NotFound/NotFound';
import { useAppDispatch, useAppSelector } from '../../app/store/store';
import { getIssues, resetCurrentIssue } from '../../entities/issue/model';
import type { IFilters } from '../../entities/issue/model';
import { Issue } from '../../types/issueInterfaces';
import getTime from '@entities/issue/lib/get-time';
import hexConverter from '@entities/issue/lib/hex-converter';
import statuses from '@entities/issue/config/statuses';
import { Select } from '@shared/ui/select';
import { Button } from '@shared/ui/button';
import CustomDatePicker from '@shared/ui/custom-datepicker';
import { selectUser } from '../../entities/auth/model';
import {
  fetchAllCompanies,
  selectCompanyList,
} from '../../entities/company/model';

const STATUS_TRANSLATE: Record<string, string> = {
  'Work in progress': 'В работе',
  Postponed: 'Отложено',
  Resolved: 'Решено',
  Closed: 'Закрыто',
};

// okdesk возвращает названия типов на английском, а в UI хочется
// читаемых русских — переводим то, что приходит, остальное показываем
// как есть.
const TYPE_TRANSLATE: Record<string, string> = {
  'Reclamation (head office)': 'Рекламация',
  Reclamation: 'Рекламация',
};

const initialFilters: IFilters = {
  startDate: null,
  endDate: null,
  status: '',
  pageNumber: 1,
  companyIds: [],
};

// Единый лейбл для полей фильтра — совпадает с лейблом
// CustomDatePicker, чтобы все подписи стояли на одной линии.
const FIELD_LABEL = 'block text-sm font-medium text-[#2d2c2c] mb-1';

// react-select по умолчанию рисует control высотой 40px, а инпут
// датапикера — 44px. Подгоняем select под 44px, иначе поля фильтра
// стоят на разной высоте.
const selectControlStyles = {
  control: () => ({ height: '44px', minHeight: '44px' }),
};

export default function Devices() {
  const [filters, setFilters] = useState<IFilters>(initialFilters);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // Накопитель okdesk-компаний, встреченных в загруженных тикетах
  // (okdesk_id → имя). Используется для опций фильтра «Компания» —
  // см. companyOptions ниже.
  const [issueCompanies, setIssueCompanies] = useState<Record<number, string>>(
    {}
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const issues = useAppSelector((state) => state.issues.issues);
  const filteredIssues = useAppSelector((state) => state.issues.filteredIssues);
  const isNextPageEmpty = useAppSelector(
    (state) => state.issues.isNextPageEmpty
  );
  const issuesStatus = useAppSelector((state) => state.issues.listFetchStatus);
  const me = useAppSelector(selectUser);
  
  const INTERNAL_GROUPS = [
    'MANAGER',
    'OFFICE_MANAGER',
    'SALES_DIRECTOR',
    'HD_HEAD',
    'HD_ENGINEER',
    'HD_SERVICE_ENGINEER',
    'admins',
  ];
  const CLIENT_GROUPS = ['COMPANY_OWNER', 'USER', 'BASIC'];
  const userGroups = me?.groups ?? [];
  const hasInternalGroup = userGroups.some((g: string) =>
    INTERNAL_GROUPS.includes(g)
  );
  const hasClientGroup = userGroups.some((g: string) =>
    CLIENT_GROUPS.includes(g)
  );
  const isStaff =
    hasInternalGroup ||
    (!hasClientGroup && Boolean(me?.is_staff || me?.is_superuser));
  // Список компаний — нужен для фильтра. Тянем только если юзер staff;
  // у клиентского юзера /companies/ всё равно вернёт максимум одну,
  // фильтр там бессмысленный.
  const companies = useAppSelector(selectCompanyList);
  useEffect(() => {
    if (isStaff) {
      dispatch(fetchAllCompanies());
    }
  }, [dispatch, isStaff]);
  const isFiltersApplied =
    JSON.stringify(filters) !== JSON.stringify(initialFilters);

  useEffect(() => {
    dispatch(getIssues({ filters, page: currentPage }));
    dispatch(resetCurrentIssue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, dispatch]);

  const okdeskIdToCompanyName = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of companies as any[]) {
      const les = c?.legal_entities ?? [];
      for (const le of les) {
        if (le?.okdesk_id) {
          map.set(le.okdesk_id, c.name);
        }
      }
    }
    return map;
  }, [companies]);

  // Копим компании из загруженных тикетов. Только staff видит фильтр и
  // мультикомпанийную выдачу. Копим (а не пересобираем по текущей странице),
  // чтобы при фильтрации по одной компании список опций не схлопывался до неё.
  useEffect(() => {
    if (!isStaff || !filteredIssues?.length) return;
    setIssueCompanies((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const it of filteredIssues as any[]) {
        const id = it?.company?.id;
        const name = it?.company?.name;
        if (id && name && !next[id]) {
          next[id] = name;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [isStaff, filteredIssues]);

  // Опции фильтра «Компания»: окдеск-компании из тикетов как базовый слой,
  // поверх — имена из нашей БД (они приоритетнее, могут отличаться от okdesk;
  // та же логика, что в колонке «Компания»). Значение опции — okdesk_id,
  // именно его ждёт бэк в company_ids. Раньше список строился ТОЛЬКО из
  // LK-компаний с okdesk_id — а он почти ни у кого не проставлен, поэтому
  // в фильтре была всего одна компания.
  const companyOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const [id, name] of Object.entries(issueCompanies)) {
      map.set(Number(id), name);
    }
    for (const [id, name] of Array.from(okdeskIdToCompanyName.entries())) {
      map.set(id, name);
    }
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
  }, [issueCompanies, okdeskIdToCompanyName]);

  const onSubmit = () => {
    setCurrentPage(1);
    dispatch(getIssues({ filters, page: 1 }));
  };

  const onFiltersReset = () => {
    setCurrentPage(1);
    setFilters(initialFilters);
    dispatch(getIssues({ filters: initialFilters, page: 1 }));
  };

  const rows = (filteredIssues ?? issues) as Issue[] | any;

  const columns: Column<any>[] = useMemo(() => {
    const base: Column<any>[] = [
      {
        key: 'id',
        header: '№',
        headerClassName: 'w-[1%] whitespace-nowrap',
        render: (it) => (
          <span className="text-[#2d2c2c] font-mono text-xs">#{it?.id}</span>
        ),
      },
      {
        key: 'title',
        header: 'Тема',
        render: (it) => (
          <div className="min-w-0">
            <p
              className="font-medium text-[#2d2c2c] truncate max-w-[420px]"
              title={it?.title}
            >
              {it?.title || 'Без темы'}
            </p>
            {it?.contact?.name ? (
              <p className="text-xs text-gray-400 truncate">
                {it.contact.name}
              </p>
            ) : null}
          </div>
        ),
      },
    ];
   
    if (isStaff) {
      base.push({
        key: 'company',
        header: 'Компания',
        render: (it) => {
          const okdeskId = it?.company?.id;
          const fromOkdesk = it?.company?.name as string | undefined;
          const fromLk = okdeskId
            ? okdeskIdToCompanyName.get(okdeskId)
            : undefined;
          const name = fromLk || fromOkdesk;
          if (!name) {
            return <span className="text-gray-400 text-xs">—</span>;
          }
          return (
            <span
              className="text-[#2d2c2c] text-xs truncate max-w-[220px] inline-block"
              title={name}
            >
              {name}
            </span>
          );
        },
      });
    }
    base.push(
      {
        key: 'type',
        header: 'Тип',
        render: (it) => (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
            {TYPE_TRANSLATE[it?.type?.name] || it?.type?.name || '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Статус',
        render: (it) => {
          const statusName = it?.status?.name;
          const color = it?.status?.color || '#6c6c6c';
          return (
            <span
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              style={{
                color,
                backgroundColor: hexConverter(color),
              }}
            >
              {STATUS_TRANSLATE[statusName] || statusName || '—'}
            </span>
          );
        },
      },
      {
        key: 'created_at',
        header: 'Поступило',
        render: (it) => (
          <span className="text-gray-600 text-xs">
            {it?.created_at ? getTime(it.created_at, false, false) : '—'}
          </span>
        ),
      },
      {
        key: 'deadline_at',
        header: 'Дедлайн',
        render: (it) => (
          <span className="text-gray-600 text-xs">
            {it?.deadline_at ? getTime(it.deadline_at, false, false) : '—'}
          </span>
        ),
      }
    );
    return base;
  }, [isStaff, okdeskIdToCompanyName]);

  return (
    <MainLayout>
      <MainLayout.Title>Устройства</MainLayout.Title>
      <MainLayout.Main>
        <Tabs variant="underline" initialActiveTab={1}>
          <div title="Мои устройства">
            <NotFound isAtWork={true} />
          </div>
          <div title="На диагностике">
         
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-[240px] min-h-[72px] flex flex-col">
                  <label className={FIELD_LABEL}>Статус</label>
                  <Select
                    placeholder="Все"
                    options={statuses}
                    isVisible={false}
                    customStyles={selectControlStyles}
                    optionSelected={
                      filters.status as
                        | import('@shared/ui/select').SelectOption
                        | null
                    }
                    isClearable={false}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onSelected={(option: any) =>
                      setFilters((prev) => ({ ...prev, status: option }))
                    }
                  />
                </div>
                <CustomDatePicker
                  label="Дата поступления"
                  filters={filters as any}
                  setFilters={setFilters as any}
                />

                {isStaff && companyOptions.length > 0 && (
                  <div className="w-[260px] min-h-[72px] flex flex-col">
                    <label className={FIELD_LABEL}>Компания</label>
                    <Select
                      placeholder="Все компании"
                      options={companyOptions}
                      isVisible={false}
                      customStyles={selectControlStyles}
                      optionSelected={
                        filters.companyIds && filters.companyIds.length > 0
                          ? companyOptions.find(
                              (o) => o.value === filters.companyIds?.[0]
                            ) || null
                          : null
                      }
                      isClearable
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onSelected={(option: any) =>
                        setFilters((prev) => ({
                          ...prev,
                          companyIds:
                            option && option.value
                              ? [Number(option.value)]
                              : [],
                        }))
                      }
                    />
                  </div>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <Button onClick={onSubmit} disabled={!isFiltersApplied}>
                    Найти
                  </Button>
                  {isFiltersApplied && (
                    <Button isWhite onClick={onFiltersReset}>
                      Сбросить
                    </Button>
                  )}
                </div>
              </div>
            </div>

           
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-6 py-4">
                <DataTable<any>
                  columns={columns}
                  data={rows ?? []}
                  keyAccessor={(it) => it?.id}
                  onRowClick={(it) => navigate(`/issues/${it?.id}`)}
                  loading={
                    issuesStatus === 'loading' && (rows?.length ?? 0) === 0
                  }
                  emptyMessage="Обращений пока нет"
                />
              </div>

              {(rows?.length ?? 0) > 0 && (
                <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-100">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ‹ Назад
                  </button>
                  <span className="text-sm text-gray-500 px-2">
                    Стр. {currentPage}
                  </span>
                  <button
                    type="button"
                    disabled={isNextPageEmpty}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Вперёд ›
                  </button>
                </div>
              )}
            </div>
          </div>
        </Tabs>
      </MainLayout.Main>
    </MainLayout>
  );
}
